import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AdminPanelSettings, Logout, VpnKey, Home, Router as RouterIcon, Devices, Wifi, Groups } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import { authenticateAdmin, adminLogout, clearAdminError } from '../../store/slices/adminAuthSlice';
import { resetAdminRoutes } from '../../store/slices/adminRouteSlice';
import GetRouteAdmin from './GetRouteAdmin';
import DeviceManagement from './DeviceManagement';
import ActiveDevices from './ActiveDevices';
import ClientGroupManagement from './ClientGroupManagement';

const AdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState(0);
  const [tempApiKey, setTempApiKey] = useState('');

  const {
    isAdminAuthenticated,
    loading: authLoading,
    error: authError,
  } = useAppSelector((state: RootState) => state.adminAuth);

  const isDirectAdminAccess = location.pathname === '/admin';

  const handleApiKeySubmit = () => {
    if (tempApiKey.trim()) {
      dispatch(authenticateAdmin({ adminApiKey: tempApiKey.trim() }));
    }
  };

  useEffect(() => {
    if (isAdminAuthenticated) {
      setTempApiKey('');
    }
  }, [isAdminAuthenticated]);

  const handleAdminLogout = () => {
    dispatch(adminLogout());
    dispatch(resetAdminRoutes());
    if (isDirectAdminAccess) {
      window.location.reload();
    } else {
      navigate('/');
    }
  };

  // Authentication gate
  if (!isAdminAuthenticated) {
    return (
      <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown PaperProps={{ sx: { minHeight: 200 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminPanelSettings color="primary" />
          Admin Authentication Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please enter your admin API key to access the admin panel.
          </Typography>

          {authError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearAdminError())}>
              {authError}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Admin API Key"
            type="password"
            value={tempApiKey}
            onChange={(e) => setTempApiKey(e.target.value)}
            disabled={authLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleApiKeySubmit();
              }
            }}
            placeholder="Enter your admin API key"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/')} disabled={authLoading} startIcon={<Home />}>
            {isDirectAdminAccess ? 'Go to Login' : 'Cancel'}
          </Button>
          <Button
            onClick={handleApiKeySubmit}
            variant="contained"
            disabled={authLoading || !tempApiKey.trim()}
            startIcon={authLoading ? <CircularProgress size={20} /> : <VpnKey />}
          >
            Authenticate
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: 'secondary.main' }}>
        <Toolbar>
          <AdminPanelSettings sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            BeeSort Service - Admin Panel
          </Typography>
          {!isDirectAdminAccess && (
            <Button color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
              Back to Dashboard
            </Button>
          )}
          {isDirectAdminAccess && (
            <Button color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }} startIcon={<Home />}>
              Login Page
            </Button>
          )}
          <IconButton color="inherit" onClick={handleAdminLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<RouterIcon />} iconPosition="start" label="Route Search" />
            <Tab icon={<Devices />} iconPosition="start" label="Device Management" />
            <Tab icon={<Groups />} iconPosition="start" label="Client Groups" />
            <Tab icon={<Wifi />} iconPosition="start" label="Active Devices" />
          </Tabs>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {tab === 0 && <GetRouteAdmin />}
        {tab === 1 && <DeviceManagement />}
        {tab === 2 && <ClientGroupManagement />}
        {tab === 3 && <ActiveDevices />}
      </Container>
    </>
  );
};

export default AdminDashboard;
